import { Component, ElementRef, HostListener, Input, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

interface CalendarDay {
  date: Date;
  outside: boolean;
  disabled: boolean;
}

@Component({
  selector: 'app-date-select',
  templateUrl: './date-select.component.html',
  styleUrl: './date-select.component.css',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateSelectComponent),
      multi: true,
    },
  ],
})
export class DateSelectComponent implements ControlValueAccessor {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() label = '';
  @Input() required = false;
  @Input() placeholder = 'dd/mm/yyyy';
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;

  readonly months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  readonly weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  selectedDate: Date | null = null;
  text = '';
  disabled = false;
  open = false;
  alignEnd = false;
  openUp = false;
  viewYear = new Date().getFullYear();
  viewMonth = new Date().getMonth();

  onChange: (value: string) => void = () => undefined;
  onTouched: () => void = () => undefined;

  get years(): number[] {
    const from = this.minDate?.getFullYear() ?? 1900;
    const to = this.maxDate?.getFullYear() ?? new Date().getFullYear() + 20;
    const list: number[] = [];
    for (let y = to; y >= from; y--) list.push(y);
    return list;
  }

  get days(): CalendarDay[] {
    const start = new Date(this.viewYear, this.viewMonth, 1 - new Date(this.viewYear, this.viewMonth, 1).getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      return { date, outside: date.getMonth() !== this.viewMonth, disabled: this.isOutOfRange(date) };
    });
  }

  isSelected(date: Date): boolean {
    return !!this.selectedDate && this.sameDay(date, this.selectedDate);
  }

  isToday(date: Date): boolean {
    return this.sameDay(date, new Date());
  }

  toggle(): void {
    if (this.disabled) return;
    if (!this.open) {
      const base = this.selectedDate ?? new Date();
      this.viewYear = base.getFullYear();
      this.viewMonth = base.getMonth();
    }
    this.open = !this.open;
    if (this.open) this.placePanel();
  }

  /** The panel is absolutely positioned, so flip it when it would overflow the viewport or a scrolling parent (e.g. a dialog body). */
  private placePanel(): void {
    const rect = this.host.nativeElement.getBoundingClientRect();
    let right = window.innerWidth;
    let bottom = window.innerHeight;
    for (let el = this.host.nativeElement.parentElement; el; el = el.parentElement) {
      const box = el.getBoundingClientRect();
      if (!box.width || !box.height) continue; // display: contents (e.g. a custom element host) has no box to clip against
      const style = getComputedStyle(el);
      // a onevedai-dialog scrolls/clips inside its shadow DOM, so treat the element itself as a clipping box
      const isDialog = el.tagName === 'ONEVEDAI-DIALOG';
      if (isDialog || /(auto|scroll|hidden)/.test(style.overflowY)) bottom = Math.min(bottom, box.bottom);
      if (isDialog || /(auto|scroll|hidden)/.test(style.overflowX)) right = Math.min(right, box.right);
    }
    const panelWidth = 296;
    const panelHeight = 340;
    this.alignEnd = rect.left + panelWidth > right - 8;
    this.openUp = rect.bottom + panelHeight > bottom && rect.top > panelHeight;
  }

  shiftMonth(delta: number): void {
    const d = new Date(this.viewYear, this.viewMonth + delta, 1);
    this.viewYear = d.getFullYear();
    this.viewMonth = d.getMonth();
  }

  setMonth(value: string): void {
    this.viewMonth = Number(value);
  }

  setYear(value: string): void {
    this.viewYear = Number(value);
  }

  pick(day: CalendarDay): void {
    if (day.disabled) return;
    this.commit(day.date);
    this.open = false;
  }

  /** Typing: auto-inserts slashes, emits a value only once dd/mm/yyyy is a real, in-range date. */
  onType(raw: string): void {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    let text = digits.slice(0, 2);
    if (digits.length > 2) text += '/' + digits.slice(2, 4);
    if (digits.length > 4) text += '/' + digits.slice(4);
    this.text = text;

    const date = digits.length === 8 ? this.parse(digits) : null;
    this.selectedDate = date && !this.isOutOfRange(date) ? date : null;
    this.onChange(this.selectedDate ? this.toIsoDate(this.selectedDate) : '');
  }

  onBlur(): void {
    // drop half-typed text that never became a valid date
    if (!this.selectedDate) this.text = '';
    this.onTouched();
  }

  clear(): void {
    this.text = '';
    this.selectedDate = null;
    this.onChange('');
    this.open = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) this.open = false;
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    this.open = false;
  }

  writeValue(value: string | Date | null): void {
    const parsed = value instanceof Date ? value : value ? new Date(value) : null;
    this.selectedDate = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
    this.text = this.selectedDate ? this.format(this.selectedDate) : '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private commit(date: Date): void {
    this.selectedDate = date;
    this.text = this.format(date);
    this.onChange(this.toIsoDate(date));
    this.onTouched();
  }

  private parse(digits: string): Date | null {
    const day = Number(digits.slice(0, 2));
    const month = Number(digits.slice(2, 4));
    const year = Number(digits.slice(4, 8));
    const date = new Date(year, month - 1, day);
    const valid = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    return valid ? date : null;
  }

  private isOutOfRange(date: Date): boolean {
    const t = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    if (this.minDate && t < new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate()).getTime()) return true;
    if (this.maxDate && t > new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate()).getTime()) return true;
    return false;
  }

  private sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  private format(date: Date): string {
    const d = `${date.getDate()}`.padStart(2, '0');
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${d}/${m}/${date.getFullYear()}`;
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
